import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { of, Subject, throwError } from 'rxjs';
import { delay, filter, retryWhen, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import get from 'lodash-es/get';

import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { ScrollimationSettings } from './scrollimation.interface';
import { ScrollToService } from '@finder/shared/services/scroll-to/scroll-to.service';
import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';
import { ScrollimationService } from '@finder/shared/services/scrollimation/scrollimation.service';
import { ScrollimationFactory } from '@finder/shared/services/scrollimation/scrollimation.factory';
import { LazyLoaderService } from '@finder/shared/services/lazy-loader/lazy-loader-service';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { CommonModule } from '@angular/common';
import { LottieComponent } from '../lottie/lottie.component';

declare const gsap: any;
declare const ScrollTrigger: any;

@Component({
    selector: 'finder-scrollimation',
    templateUrl: './scrollimation.component.html',
    styleUrls: ['./scrollimation.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        LottieComponent,
    ],
    providers: [
        {
            provide: ScrollimationService,
            useFactory: ScrollimationFactory,
            deps: [LazyLoaderService, LoggerService, DebugLogService]
        },
    ]
})
export class ScrollimationComponent implements OnInit, OnDestroy {
    @Input() data: ScrollimationSettings;
    @Output() scrollLeave = new EventEmitter<boolean>();
    @Output() scrollEnter = new EventEmitter<boolean>();

    playbackTrigger = new EventEmitter<boolean>();
    animation: any;
    lottie;

    private totalFrames = 0;
    private destroy$ = new Subject<void>();
    private scrollTrigger;
    private lottieChild;

    /* istanbul ignore next */
    get element() {
        return this.el.nativeElement;
    }

    /* istanbul ignore next */
    get lottieEl() {
        return this.lottieChild.el.nativeElement;
    }

    constructor(
        private el: ElementRef,
        private windowRef: WindowRef,
        private scrollToService: ScrollToService,
        private debugLog: DebugLogService,
        private scrollimationService: ScrollimationService,
    ) {}

    ngOnInit() {
        this.scrollimationService.isReady()
            .pipe(
                filter(isReady => isReady),
                switchMap(() => this.getLottie()),
                retryWhen(errors => {
                    this.debugError('lottie not found, retrying...');

                    return errors.pipe(delay(10), take(3));
                }),
            )
            .subscribe({
                next: (lottie) => {
                    this.initLottie(lottie);
                },
                error: (error) => {
                    this.debugLog.error(error);
                }
            });
    }

    // Ignore just for coverage, there's no need to test unsubscribe
    /* istanbul ignore next */
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getReference(reference) {
        this.lottieChild = reference;
    }

    private initLottie(lottie) {
        this.lottie = lottie;
        if (this.lottie.disabled === true) {
            this.debugOut('lottie disabled');

            return;
        }

        // Get the total frames so we can set up the scroll
        this.totalFrames = this.lottie.totalFrames;

        // Set container height
        this.element.style.setProperty('--scrollimation-height', this.data.containerHeight || '100%');


        this.debugOut(`lottie total frames::`, this.totalFrames);
        this.lottie.goToAndStop(0, true);
        this.createScrollTrigger();
    }

    private getLottie() {
        this.animation = this.lottieEl.querySelector('lottie-player');
        const lottie = get(this.animation, '_lottie');
        if (lottie) {
            return of(lottie);
        } else {
            throwError(() => new Error('Unable to find lottie element'));
        }
    }

    /* istanbul ignore next */
    private createScrollTrigger() {
        if (!ScrollTrigger) {
            this.debugError(`Unable to fid 'ScrollTrigger'!`);

            return;
        }

        // Set values
        const maxFrame = this.totalFrames - 1;
        const trigger = this.data.trigger || '.scrollimation';
        const startPosition = this.data.startPosition || 'top center';
        const scrub = this.data.scrub === false ? false : this.data.scrub || true;
        const pin = this.data.pin || false;
        const duration = this.data.duration || false;
        const end = `+=${this.data.end || 500}`;
        const timeObj = { currentFrame: 0 };

        this.debugOut(`gsap::`, gsap);
        this.debugOut(`ScrollTrigger::`, ScrollTrigger);
        this.debugOut(`vars::`, {
            maxFrame, trigger, startPosition, scrub, pin, duration, end, timeObj
        });

        // Create scroll trigger
        // Docs: https://greensock.com/docs/v3/Plugins/ScrollTrigger
        this.scrollTrigger = ScrollTrigger.create({
            trigger,
            scrub,
            pin,
            start: startPosition,
            end,
            toggleActions: scrub ? 'play pause resume reset' : 'play none resume none',
            onUpdate: ({ progress }) => this.scrollUpdate(progress, duration, timeObj, maxFrame),
            onLeave: () => {
                if (scrub === false) {
                    return;
                }

                this.scrollLeave.emit(true);
            },
            onLeaveBack: () => {
                if (scrub === false) {
                    return;
                }

                this.scrollLeave.emit(false);
            },
            onEnter: self => {
                this.scrollEnter.emit(true);

                // Debug output
                this.debugOut(`ScrollTrigger onEnter:`, this.lottie);

                if (scrub !== false) {
                    return;
                }

                if (this.data.enableScrollLock) {
                    this.enableScrollLock(self);
                }

                // Turn off scroll trigger
                self.disable();

                this.lottie.onComplete = () => {
                    if (this.data.enableScrollLock) {
                        this.removeScrollLock();
                    }

                    // Emit the leave event for any listeners
                    this.scrollLeave.emit(true);
                };

                // Start lottie playback
                this.playbackTrigger.emit(true);
            }
        });

        // Debug output
        this.debugOut(`***** ScrollTrigger settings:`, this.scrollTrigger);
    }

    /* istanbul ignore next */
    private scrollUpdate(progress, duration, timeObj, maxFrame) {
        const currentFrame = Math.floor(progress * maxFrame);
        if (duration) {
            gsap.to(timeObj, {
                duration,
                currentFrame,
                onUpdate: () => {
                    this.lottie.goToAndStop(timeObj.currentFrame, true);
                },
                ease: 'none',
            });
        } else {
            this.lottie.goToAndStop(currentFrame, true);
        }
    }

    private enableScrollLock(self) {
        const window = this.windowRef.nativeWindow;
        const document = window.document;
        const triggerEl = self.trigger;

        // Check scroll position
        const docTop = document.documentElement.scrollTop;
        const triggerTop = triggerEl.getBoundingClientRect().top + window.scrollY;

        // If we are scrolled farther down the page reset to trigger top
        if (docTop > triggerTop) {
            this.scrollToService.scrollToElement(triggerEl, true);
        }

        // Prevent scrolling
        const scrollbarWidth = (window.innerWidth - document.body.clientWidth) + 'px';
        document.body.style.setProperty('overflow', 'hidden');
        document.body.style.setProperty('padding-right', scrollbarWidth);
        document.body.style.setProperty('cursor', 'wait');
    }

    private removeScrollLock() {
        const document = this.windowRef.nativeWindow.document;

        // Remove body overflow to re-enable scrolling
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');
        document.body.style.removeProperty('cursor');
    }

    private debugOut(msg, obj?) {
        if (obj) {
            this.debugLog.log(`***** Scrollimation - ${msg}`, obj);
        } else {
            this.debugLog.log(`***** Scrollimation - ${msg}`);
        }
    }

    private debugError(msg, obj?) {
        if (obj) {
            this.debugLog.error(`***** Scrollimation - ${msg}`, obj);
        } else {
            this.debugLog.error(`***** Scrollimation - ${msg}`);
        }
    }
}
